<script lang="ts">
  import { applicationRoleLabel } from './lib/utils/roleLabels';
  import { user, logout } from './lib/stores/user';
  import { 
    User, Mail, Phone, Building2, MapPin, Shield, ShieldCheck, 
    KeyRound, ExternalLink, Sparkles, CheckCircle2, AlertTriangle, 
    LogOut, Clock, Layers, ArrowUpRight, Lock, Copy, Check
  } from 'lucide-svelte';

  let copiedId = $state(false);

  function copyUserId() {
    if (!$user?.user_id) return;
    navigator.clipboard.writeText(String($user.user_id));
    copiedId = true;
    setTimeout(() => { copiedId = false; }, 2000);
  }

  function getRoleBadgeClass(role?: string) {
    switch (role) {
      case 'system_admin':
        return 'badge-error text-error-content font-bold shadow-xs';
      case 'customer':
      case 'customer_representative':
        return 'badge-primary text-primary-content font-bold shadow-xs';
      case 'property_manager':
        return 'badge-secondary text-secondary-content font-bold shadow-xs';
      case 'installer':
        return 'badge-warning text-warning-content font-bold shadow-xs';
      default:
        return 'badge-ghost text-base-content font-semibold';
    }
  }

  function getRoleDescription(role?: string) {
    switch (role) {
      case 'system_admin':
        return 'Full administrative access across all fleet devices, telemetry pipelines, company accounts, and security audit policies.';
      case 'customer':
      case 'customer_representative':
        return 'Authorized representative for customer accounts, property allocations, and organizational water consumption oversight.';
      case 'property_manager':
        return 'Site-level operations management, unit occupancy monitoring, and device telemetry inspection for assigned properties.';
      case 'installer':
        return 'Field technician permissions for meter commissioning, physical hardware verification, and installation job execution.';
      case 'tenant':
        return 'Resident access for personal unit water consumption monitoring, conservation insights, and leak alert notifications.';
      default:
        return 'Standard system observer permissions for read-only telemetry and analytics inspection.';
    }
  }
</script>

<div class="space-y-6 max-w-6xl mx-auto pb-12">
  <!-- Page Header -->
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-base-content/10">
    <div class="flex items-center gap-3">
      <div class="p-2 rounded-xl bg-primary/10 text-primary">
        <User class="w-5 h-5" />
      </div>
      <div>
        <h1 class="text-2xl md:text-3xl font-extrabold tracking-tight text-base-content">
          My Profile
        </h1>
        <p class="text-xs text-base-content/60">
          User identity, corporate affiliations, and assigned real estate properties.
        </p>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <span class="badge badge-success badge-sm font-bold gap-1 text-[11px]">
        <CheckCircle2 class="w-3 h-3" /> Account Active
      </span>
    </div>
  </div>

  <!-- Executive Profile Header Card -->
  <div class="card bg-base-100/80 backdrop-blur-md border border-base-content/10 shadow-sm rounded-3xl overflow-hidden">
    <div class="bg-gradient-to-r from-primary/10 via-base-200/50 to-secondary/10 p-6 md:p-8 border-b border-base-content/5">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="flex items-center gap-5">
          <div class="relative">
            <div class="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-primary/20 text-primary border-2 border-primary/30 flex items-center justify-center font-black text-3xl shadow-inner">
              {#if $user?.name}
                {$user.name.slice(0, 2).toUpperCase()}
              {:else}
                <User class="w-10 h-10" />
              {/if}
            </div>
            <div class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 border-2 border-base-100 flex items-center justify-center shadow-xs" title="Account Active">
              <Check class="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <div>
            <div class="flex flex-wrap items-center gap-2 mb-1.5">
              <h2 class="text-xl md:text-2xl font-extrabold tracking-tight text-base-content">
                {$user?.name || 'Anonymous User'}
              </h2>
              <span class="badge badge-md uppercase tracking-wider text-[11px] {getRoleBadgeClass($user?.user_type)}">
                {applicationRoleLabel($user?.user_type)}
              </span>
              {#if $user?.impersonation}
                <span class="badge badge-warning badge-sm font-bold gap-1 animate-pulse">
                  <Shield class="w-3 h-3" /> Impersonated Session
                </span>
              {/if}
            </div>
            <p class="text-sm text-base-content/70 flex items-center gap-2">
              <Mail class="w-4 h-4 text-base-content/40 shrink-0" />
              <span>{$user?.email || 'No email registered'}</span>
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <a href="#/settings" class="btn btn-outline btn-sm rounded-xl font-bold gap-1.5">
            <Sparkles class="w-3.5 h-3.5" /> Preferences
          </a>
          <button onclick={logout} class="btn btn-ghost btn-sm rounded-xl text-error hover:bg-error/10 font-bold gap-1.5">
            <LogOut class="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </div>
    </div>

    <!-- Role Scope & Operational Context Banner -->
    <div class="px-6 py-4 bg-base-content/[0.02] flex items-start gap-3 text-xs text-base-content/70">
      <ShieldCheck class="w-4 h-4 text-primary shrink-0 mt-0.5" />
      <div>
        <span class="font-bold text-base-content">Privilege Scope:</span> {getRoleDescription($user?.user_type)}
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Left Column: Identity & Corporate Affiliation -->
    <div class="space-y-6 lg:col-span-1">
      <!-- Identity & Security Metadata Card -->
      <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5">
        <h2 class="text-xs font-black uppercase tracking-wider text-base-content/50 mb-4 flex items-center gap-2">
          <Lock class="w-3.5 h-3.5 text-primary" /> Identity & Credentials
        </h2>
        <dl class="space-y-3.5 text-xs">
          <div class="flex justify-between items-center py-1.5 border-b border-base-content/5">
            <dt class="font-semibold text-base-content/60">User Identifier</dt>
            <dd class="font-mono font-bold flex items-center gap-1.5">
              <span>#{$user?.user_id ?? '—'}</span>
              {#if $user?.user_id}
                <button onclick={copyUserId} class="btn btn-ghost btn-xs btn-circle text-base-content/50 hover:text-base-content" title="Copy User ID">
                  {#if copiedId}
                    <Check class="w-3 h-3 text-emerald-500" />
                  {:else}
                    <Copy class="w-3 h-3" />
                  {/if}
                </button>
              {/if}
            </dd>
          </div>
          <div class="flex justify-between items-center py-1.5 border-b border-base-content/5">
            <dt class="font-semibold text-base-content/60">Phone</dt>
            <dd class="font-medium text-base-content/90">{$user?.phone || 'Not provided'}</dd>
          </div>
          <div class="flex justify-between items-center py-1.5 border-b border-base-content/5">
            <dt class="font-semibold text-base-content/60">Global Role</dt>
            <dd class="font-bold text-base-content capitalize">{applicationRoleLabel($user?.user_type)}</dd>
          </div>
          <div class="flex justify-between items-center py-1.5 border-b border-base-content/5">
            <dt class="font-semibold text-base-content/60">Status</dt>
            <dd class="badge badge-success badge-sm text-[10px] font-bold">Active</dd>
          </div>
          <div class="flex justify-between items-center py-1.5">
            <dt class="font-semibold text-base-content/60">Password & Security</dt>
            <dd>
              <a href="#/password/recovery" class="text-primary hover:underline font-bold text-xs flex items-center gap-1">
                Reset Key <ArrowUpRight class="w-3 h-3" />
              </a>
            </dd>
          </div>
        </dl>
      </div>

      <!-- Corporate Affiliation Card -->
      <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5">
        <h2 class="text-xs font-black uppercase tracking-wider text-base-content/50 mb-4 flex items-center gap-2">
          <Building2 class="w-3.5 h-3.5 text-secondary" /> Affiliated Organization
        </h2>
        {#if $user?.company}
          <div class="p-4 rounded-xl bg-base-content/[0.02] border border-base-content/5 space-y-3">
            <div class="flex items-center justify-between">
              <span class="font-bold text-base text-base-content">{$user.company.company_name}</span>
              <span class="badge badge-ghost badge-sm text-[10px] uppercase font-bold">{$user.company.company_type}</span>
            </div>
            <p class="text-xs text-base-content/60">Company Record ID: <span class="font-mono font-semibold">#{$user.company.company_id}</span></p>
          </div>
        {:else}
          <div class="p-4 rounded-xl bg-base-200/40 text-center text-xs text-base-content/60 border border-dashed border-base-content/10">
            No corporate entity directly assigned to this account.
          </div>
        {/if}
        <div class="mt-4 pt-3 border-t border-base-content/5 text-[11px] text-base-content/50 flex items-center gap-1.5">
          <Clock class="w-3.5 h-3.5 shrink-0" />
          <span>Contact your administrator to modify organizational membership.</span>
        </div>
      </div>

      <!-- Customer Account Memberships (if present) -->
      {#if $user?.customer_accounts && $user.customer_accounts.length > 0}
        <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5">
          <h2 class="text-xs font-black uppercase tracking-wider text-base-content/50 mb-4 flex items-center gap-2">
            <Layers class="w-3.5 h-3.5 text-emerald-500" /> Customer Accounts
          </h2>
          <div class="space-y-2.5">
            {#each $user.customer_accounts as acct}
              <div class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 flex items-center justify-between">
                <div>
                  <p class="font-bold text-xs text-base-content">{acct.name}</p>
                  <p class="text-[10px] text-base-content/50 font-mono">Scope: {acct.access_scope.replace('_', ' ')}</p>
                </div>
                <span class="badge badge-sm badge-outline text-[10px] font-semibold">{acct.role.replace('_', ' ')}</span>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>

    <!-- Right Column: Assigned Properties Portfolio -->
    <div class="space-y-6 lg:col-span-2">
      <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5 md:p-6">
        <div class="flex items-center justify-between mb-5">
          <div>
            <h2 class="text-base font-bold text-base-content flex items-center gap-2">
              <MapPin class="w-4 h-4 text-primary" /> Assigned Properties
            </h2>
            <p class="text-xs text-base-content/60 mt-0.5">Real estate sites and multi-tenant facilities managed or occupied by this account</p>
          </div>
          <span class="badge badge-neutral badge-sm font-bold">
            {$user?.properties?.length || 0} Sites
          </span>
        </div>

        {#if $user?.properties && $user.properties.length > 0}
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {#each $user.properties as property}
              <div class="p-4 rounded-xl border border-base-content/10 bg-base-100 hover:border-primary/40 hover:shadow-xs transition-all flex flex-col justify-between group">
                <div>
                  <div class="flex items-start justify-between gap-2 mb-2">
                    <span class="font-extrabold text-sm text-base-content group-hover:text-primary transition-colors">
                      {property.property_name || `Property #${property.property_id}`}
                    </span>
                    <span class="badge badge-sm badge-ghost text-[10px] font-mono shrink-0">
                      #{property.property_id}
                    </span>
                  </div>
                  <div class="flex flex-wrap items-center gap-1.5 text-xs text-base-content/70 mb-3">
                    <span class="badge badge-xs badge-primary/20 text-primary font-bold uppercase tracking-wider text-[9px] py-1">
                      {property.role}
                    </span>
                    {#if (property as any).unit}
                      <span class="badge badge-xs badge-neutral text-[9px] py-1">
                        Unit {(property as any).unit}
                      </span>
                    {/if}
                  </div>
                </div>

                <div class="pt-2 border-t border-base-content/5 flex items-center justify-between text-xs">
                  <span class="text-base-content/50 text-[11px]">Real-time telemetry</span>
                  <a 
                    href="#/admin/properties/{property.property_id}" 
                    class="btn btn-ghost btn-xs text-primary font-bold gap-1 hover:bg-primary/10 rounded-lg"
                  >
                    Open Workspace <ExternalLink class="w-3 h-3" />
                  </a>
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="text-center py-12 px-4 border-2 border-dashed border-base-content/10 rounded-2xl bg-base-200/20">
            <div class="w-12 h-12 rounded-full bg-base-content/5 flex items-center justify-center mx-auto mb-3 text-base-content/40">
              <MapPin class="w-6 h-6" />
            </div>
            <h3 class="font-bold text-sm text-base-content mb-1">No Properties Assigned</h3>
            <p class="text-xs text-base-content/60 max-w-sm mx-auto mb-4">
              Your account currently has no site property allocations. Contact your system administrator to assign real estate sites or tenant units.
            </p>
            {#if $user?.user_type === 'system_admin'}
              <a href="#/admin/properties" class="btn btn-primary btn-sm rounded-xl font-bold">
                Explore All Sites →
              </a>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Quick Operational Workflows for User Role -->
      <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5">
        <h2 class="text-xs font-black uppercase tracking-wider text-base-content/50 mb-3 flex items-center gap-2">
          <Sparkles class="w-3.5 h-3.5 text-warning" /> Available Workflows
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {#if $user?.user_type === 'system_admin'}
            <a href="#/admin" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-primary/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                System Command <ArrowUpRight class="w-3 h-3 text-primary" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Fleet overview, audit trail, and health metrics</p>
            </a>
            <a href="#/admin/users" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-primary/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                User Management <ArrowUpRight class="w-3 h-3 text-primary" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Provision accounts, invite users, and audit roles</p>
            </a>
            <a href="#/devices" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-primary/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                Device Fleet & Allocation <ArrowUpRight class="w-3 h-3 text-primary" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Onboard hardware and assign properties</p>
            </a>
          {:else if $user?.user_type === 'installer'}
            <a href="#/installer/jobs" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-warning/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                Installer Jobs <ArrowUpRight class="w-3 h-3 text-warning" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Meter commissioning and evidence submission</p>
            </a>
            <a href="#/devices" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-warning/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                Hardware Grid <ArrowUpRight class="w-3 h-3 text-warning" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Inspect telemetry and signal RSSI</p>
            </a>
          {:else}
            <a href="#/" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-primary/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                Dashboard <ArrowUpRight class="w-3 h-3 text-primary" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">Water consumption and leak detection status</p>
            </a>
            <a href="#/devices" class="p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5 hover:bg-base-content/5 hover:border-primary/30 transition-all block">
              <p class="font-bold text-xs text-base-content flex items-center justify-between">
                Devices <ArrowUpRight class="w-3 h-3 text-primary" />
              </p>
              <p class="text-[11px] text-base-content/60 mt-1">View meter status and valve control</p>
            </a>
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>
